import { pick } from '@lc/libutils';
import { MaplibreMouseEvent } from '../events/mouse.js';
import { add } from '../utils.js';
import { MaplibreObject } from './base.js';
export class MaplibreGeojson extends MaplibreObject {
    #map;
    #geometry;
    #type;
    #libmapLayer;
    #events = [];
    constructor(id, map, geometry, type, libmapLayer) {
        super('GeojsonObject', id);
        this.#map = map;
        this.#geometry = geometry;
        this.#type = type;
        this.#libmapLayer = libmapLayer;
    }
    get attached() {
        return !!this.#map;
    }
    remove() {
        if (!this.#map || this.#map._removed)
            return;
        const layer = this.#layer;
        for (const { event, handler } of this.#events) {
            this.#map.off(event, this.id, handler);
        }
        this.#events.splice(0, this.#events.length);
        this.#map.removeLayer(this.id);
        this.#map.removeSource(layer.source);
        this.#map = null;
    }
    get #layer() {
        if (!this.#map || this.#map._removed)
            return;
        return this.#map.getLayer(this.id);
    }
    get #source() {
        if (!this.#map || this.#map._removed)
            return;
        const layer = this.#layer;
        if (!layer)
            return;
        return this.#map.getSource(layer.source);
    }
    get geometry() {
        return this.#geometry;
    }
    setGeometry(geometry) {
        const source = this.#source;
        if (!source)
            return;
        source.setData(geometry);
        this.#geometry = geometry;
    }
    get drawType() {
        return this.#type;
    }
    get paint() {
        return this.#layer?.paint ?? {};
    }
    setPaint(paint) {
        const layer = this.#layer;
        if (!layer)
            return;
        for (const [prop, value] of Object.entries(paint)) {
            layer.setPaintProperty(prop, value);
        }
    }
    get layout() {
        return this.#layer?.layout ?? {};
    }
    setLayout(layout) {
        const layer = this.#layer;
        if (!layer)
            return;
        for (const [prop, value] of Object.entries(layout)) {
            layer.setLayoutProperty(prop, value);
        }
    }
    get minZoom() {
        return this.#layer?.minzoom ?? 0;
    }
    setMinZoom(minZoom) {
        if (!this.#map || this.#map._removed)
            return;
        const layer = this.#layer;
        if (!layer)
            return;
        this.#map.setLayerZoomRange(this.id, minZoom, this.maxZoom);
    }
    get maxZoom() {
        return this.#layer?.maxzoom ?? 24;
    }
    setMaxZoom(maxZoom) {
        if (!this.#map || this.#map._removed)
            return;
        const layer = this.#layer;
        if (!layer)
            return;
        this.#map.setLayerZoomRange(this.id, this.minZoom, maxZoom);
    }
    get layer() {
        return this.#libmapLayer;
    }
    setLayer(layer) {
        if (!this.#map || this.#map._removed)
            return;
        if (!this.#layer?.id)
            return;
        this.#map.moveLayer(this.#layer.id, layer);
        this.#libmapLayer = layer;
    }
    onEvent(event, callback) {
        const map = this.#map;
        if (!map || map._removed)
            return () => { };
        const id = this.id;
        const handler = MaplibreMouseEvent.convert(callback);
        const sub = map.on(event, id, handler);
        this.#events.push({ event, handler });
        return () => sub.unsubscribe();
    }
}
export const addGeojson = add('geojson', (map, options, id) => {
    if (map._removed)
        throw new Error('Map is removed');
    let layout = options.layout ?? {};
    let paint = options.paint ?? {};
    const layer = options.layer ?? '#top';
    switch (options.drawType) {
        case 'fill':
            paint = pick(paint, 'fill-color', 'fill-opacity', 'fill-outline-color', 'fill-antialias');
            layout = {};
            break;
        case 'line':
            paint = pick(paint, 'line-color', 'line-width', 'line-opacity', 'line-dasharray');
            layout = pick(layout, 'line-cap', 'line-join');
            break;
        case 'symbol':
            if (layout['text-field'] && !layout['text-font'])
                layout = {
                    ...layout,
                    'text-font': [
                        `Noto Sans ${layout['text-weight'] ?? 'Regular'}`,
                    ],
                };
            if (layout['icon-image'] &&
                typeof layout['icon-allow-overlap'] !== 'boolean')
                layout = { ...layout, 'icon-allow-overlap': true };
            paint = pick(paint, 'text-color', 'icon-opacity', 'text-halo-color', 'text-halo-width');
            layout = pick(layout, 'text-anchor', 'text-field', 'text-font', 'text-size', 'text-allow-overlap', 'icon-allow-overlap', 'icon-anchor', 'icon-image', 'icon-padding', 'icon-rotate', 'icon-rotation-alignment', 'icon-size');
            break;
    }
    map.addLayer({
        id,
        source: {
            type: 'geojson',
            data: options.geometry,
        },
        type: options.drawType,
        layout,
        paint,
        minzoom: options.minZoom ?? 0,
        maxzoom: options.maxZoom ?? 24,
    }, layer);
    return new MaplibreGeojson(id, map, options.geometry, options.drawType, layer);
});
//# sourceMappingURL=data:application/json;base64,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